import { tenantAwareApiWrapper, withServiceClient } from './portal-http';

import { copilotFeedbackClient } from '@terra/shared-rest-clients-copilot-feedback';
import { signalFeedbackClient } from '@terra/shared-rest-clients-signal-feedback';
import type { CopilotFeedbackContract } from '@terra/shared-contracts-copilot-feedback';
import type { SignalFeedbackContract } from '@terra/shared-contracts-signal-feedback';
import {
  type FeedbackFailureArea,
  type FeedbackFailureCategory,
} from '@terra/shared-contracts-signal-feedback';
import {
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';

export const portalFeedbackApi = {
  submitCoPilotFeedback: ({
    applicationId,
    threadId,
    traceId,
    rating,
    feedback,
    screenshot,
  }: {
    applicationId: string;
    threadId: string;
    traceId: string;
    rating?: 'positive' | 'negative';
    feedback?: string;
    screenshot?: { data: string; mimeType: string };
  }) =>
    tenantAwareApiWrapper<typeof CopilotFeedbackContract.submitFeedback>({
      endpointFn: (headers) => {
        let screenshotBlob: File | undefined;

        if (screenshot) {
          const byteString = atob(screenshot.data);
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const uint8Array = new Uint8Array(arrayBuffer);

          for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
          }

          screenshotBlob = new File([uint8Array], 'screenshot.png', {
            type: screenshot.mimeType,
          });
        }

        return copilotFeedbackClient.submitFeedback({
          body: {
            applicationId,
            threadId,
            traceId,
            ...(rating ? { rating } : {}),
            ...(feedback ? { feedback } : {}),
            ...(screenshotBlob ? { screenshot: screenshotBlob } : {}),
          },
          ...withServiceClient(headers, 'chat'),
        });
      },
    }),

  submitSignalFeedback: ({
    applicationId,
    signalId,
    previousStage,
    previousStatus,
    newStage,
    newStatus,
    rating,
    feedback,
    isFalsePositive,
    failureCategory,
    failureArea,
  }: {
    applicationId: string;
    signalId: string;
    previousStage: SignalStageType;
    previousStatus: SignalStatusType | null;
    newStage: SignalStageType;
    newStatus: SignalStatusType | null;
    rating?: 'positive' | 'negative';
    feedback?: string;
    isFalsePositive?: boolean;
    failureCategory?: FeedbackFailureCategory;
    failureArea?: FeedbackFailureArea;
  }) =>
    tenantAwareApiWrapper<typeof SignalFeedbackContract.submitFeedback>({
      endpointFn: (headers) =>
        signalFeedbackClient.submitFeedback({
          body: {
            applicationId,
            signalId,
            previousStage,
            previousStatus: previousStatus ?? null,
            newStage,
            newStatus: newStatus ?? null,
            ...(rating ? { rating } : {}),
            ...(feedback ? { feedback } : {}),
            ...(failureCategory ? { failureCategory } : {}),
            ...(failureArea ? { failureArea } : {}),
            ...(isFalsePositive ? { isFalsePositive: true } : {}),
          },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  // Interact endpoints
};
